import { ThemeConfiguration, useTrainingAtom } from '@/atoms/useTrainingAtom';
import { ModelSummary, useCartAtom } from '@/atoms/useCartAtom';
import { AspectRatio, PhotoSize } from '../types/photo';
import { useCallback } from 'react';
import { api } from '@/trpc/react';
import { useSession } from '@hooks/index';
import { useRouter } from 'next/navigation';

type Mode = 'training' | 'cart';

interface UsePhotoPackActionProps {
  mode: Mode;
}

// Define ModelInfo as a looser version of Model that contains what PhotoPackDetails needs
interface ModelInfo {
  id: string;
  name?: string;
  version?: string;
  trainingMeta?: any;
  attributes?: {
    gender?: 'male' | 'female';
    [key: string]: any;
  } | null;
  [key: string]: any; // Allow other properties
}

interface PhotoPackActionHook {
  themes: ThemeConfiguration[];
  addTheme: (theme: ThemeConfiguration) => void;
  removeTheme: (themeId: string) => void;
  updateThemePhotoSize: (themeId: string, aspectRatio: AspectRatio, size: PhotoSize) => void;
  updateThemeQuantity: (themeId: string, quantity: number) => void;
  updateThemeSelectedModifiers: (
    themeId: string,
    modifiers: Record<string, Record<string, { value: string; color?: string }>>
  ) => void;
  updateThemePhotoQuantity: (themeId: string, quantity: number) => void;
  updateThemeWorkflow?: (themeId: string, workflowName: string) => void;
  updateThemeGarmentImages?: (
    themeId: string,
    garmentImages: { url: string; type: 'top' | 'bottom' | 'one-piece' }[]
  ) => void;
  bulkAddTheme?: (theme: ThemeConfiguration) => void;
  // Add new model-related methods and properties
  model: ModelInfo | undefined;
  checkModelAvailability: () => {
    hasModels: boolean;
    hasMultipleModels: boolean;
    models?: ModelSummary[] | undefined;
  };
  // New handler for theme selection that encapsulates model selection logic
  handleThemeSelection: (
    theme: Pick<ThemeConfiguration, 'id'>,
    options?: {
      onThemeSelected?: (themeId: string) => void;
      creditCheck?: (themeId: string) => boolean;
      themesAllowed?: number;
      teamId?: string;
    }
  ) => Promise<boolean>;
}

function usePhotoPackAction({ mode }: UsePhotoPackActionProps): PhotoPackActionHook {
  const trainingAtom = useTrainingAtom();
  const cartAtom = useCartAtom();
  const { isLoggedIn, user } = useSession();
  const userCreditsQuery = api.userCredits.get.useQuery({ userId: user?.id }, { enabled: !!user?.id });
  const router = useRouter();

  // Get user models query if in cart mode
  const userModelsQuery = api.model.getUserModels.useQuery({}, { enabled: mode === 'cart' && !!isLoggedIn });

  // Get the themes based on the mode
  const themes = mode === 'training' ? trainingAtom.training.themes || [] : cartAtom.cart.themes || [];

  // Get model information based on the mode
  const model: ModelInfo | undefined =
    mode === 'training'
      ? {
          id: 'training-model',
          attributes: {
            gender: trainingAtom.training.attributes.gender as 'male' | 'female'
          },
          trainingMeta: {
            // Any training metadata needed for PhotoPackDetails
          }
        }
      : (cartAtom.cart.model as ModelInfo | undefined);

  const addTheme = (theme: ThemeConfiguration) => {
    if (mode === 'training') {
      // Assuming handleThemeSelected is similar to addTheme
      // from Step2 component in the training context
      // (You'll need to fill in the actual logic here)
      const { themes } = trainingAtom.training;

      // Placeholder logic; adapt to your specific needs
      const newTheme: ThemeConfiguration = {
        ...(theme as ThemeConfiguration),
        // Ensure required properties are populated or handled, e.g.:
        quantity: 1, // Provide a default value, fetch it, etc.
        photoSize: {
          aspectRatio: theme.photoSize?.aspectRatio || '1:1',
          size: theme.photoSize?.size || 'small'
        } // default value
      };

      trainingAtom.setThemes([...themes, newTheme]);
    } else if (mode === 'cart') {
      // Assuming handleThemeSelected adds the theme to the cart
      // (You'll need to fill in the actual logic here)
      const { addTheme } = cartAtom;

      const newTheme: ThemeConfiguration = {
        ...(theme as ThemeConfiguration),
        // Ensure required properties are populated or handled, e.g.:
        quantity: 1, // Provide a default value, fetch it, etc.
        photoSize: {
          aspectRatio: theme.photoSize?.aspectRatio || '1:1',
          size: theme.photoSize?.size || 'small'
        } // default value
      };

      addTheme(newTheme);
    }
  };

  // Comprehensive handler for theme selection that encapsulates model selection logic
  const handleThemeSelection = useCallback(
    async (
      theme: Pick<ThemeConfiguration, 'id'>,
      options?: {
        onThemeSelected?: (themeId: string) => void;
        creditCheck?: (themeId: string) => boolean;
        themesAllowed?: number;
        teamId?: string;
      }
    ) => {
      if (mode === 'cart' && !isLoggedIn) {
        router?.push('/login?redirect=/pricing');
        return false;
      }
      // Check if this theme is already in the cart/theme list
      const existingTheme = themes?.find(t => t.id === theme.id);
      const isUpdating = !!existingTheme;
      // Training mode validation - check theme limits
      if (mode === 'training' && !isUpdating) {
        const currentAmountOfPacksOnCart = themes.reduce((acc, theme) => {
          return acc + (theme.quantity || 1);
        }, 0);

        const themesAllowed = options?.themesAllowed || 1; // Default to 1 if not provided
        if (currentAmountOfPacksOnCart >= themesAllowed) {
          if (options?.teamId) {
            alert(
              "You've reached your team's photo pack credit limit. Please contact your workspace owner for more credits."
            );
            return false;
          } else {
            alert(`You have reached the limit of photo packs - You can only have ${themesAllowed} photo packs`);
            return false;
          }
        } else {
          if (options?.onThemeSelected) {
            options.onThemeSelected(theme.id);
          }

          return true;
        }
      }

      // Cart mode validation - Use external creditCheck function provided by the caller
      // This allows components to maintain their own validation logic for cart mode
      if (mode === 'cart' && options?.creditCheck && !isUpdating) {
        const canProceed = options.creditCheck(theme.id);
        if (!canProceed) {
          return false;
        }
      }

      // If we're in cart mode and don't have a model selected, handle model selection first
      if (mode === 'cart' && !model) {
        const { hasModels } = checkModelAvailability();

        if (!hasModels) {
          // No models available, handle appropriately
          if (userCreditsQuery.data?.modelCredits === 0) {
            router?.push('/pricing');
          } else {
            router?.push('/train');
          }
          return false;
        } else if (userModelsQuery.data?.models.length === 1) {
          // If only one model is available, select it automatically

          cartAtom.setModel({
            avatarUrl: userModelsQuery.data.models[0]?.avatarUrl!,
            id: userModelsQuery.data.models[0]?.id!,
            name: userModelsQuery.data.models[0]?.name!,
            version: userModelsQuery.data.models[0]?.version!,
            teamId: userModelsQuery.data.models[0]?.teamId!,
            modelRepositoryId: userModelsQuery.data.models[0]?.modelRepositoryId!,
            readableName: userModelsQuery.data.models[0]?.readableName!,
            status: userModelsQuery.data.models[0]?.status!
          });
        }
      }

      // Theme can be selected now
      if (options?.onThemeSelected) {
        options.onThemeSelected(theme.id);
      }

      return true;
    },
    [themes, mode, model, userModelsQuery.data?.models, cartAtom.setModel]
  );

  // Utility method to check model availability
  const checkModelAvailability = useCallback(() => {
    // For cart mode, check actual user models
    if (mode === 'cart') {
      const models = userModelsQuery.data?.models;
      const hasModels = !!models && models.length > 0;
      const hasMultipleModels = !!models && models.length > 1;

      return {
        hasModels,
        hasMultipleModels,
        models: models
      };
    } else {
      // For training mode, a model is being created, so we don't need to check
      return {
        hasModels: true,
        hasMultipleModels: false,
        models: undefined
      };
    }
  }, [mode, userModelsQuery.data?.models]);

  const removeTheme = (themeId: string) => {
    if (mode === 'training') {
      trainingAtom.removeTheme(themeId);
    } else if (mode === 'cart') {
      cartAtom.removeTheme(themeId);
    }
  };

  const updateThemePhotoSize = (themeId: string, aspectRatio: AspectRatio, size: PhotoSize) => {
    if (mode === 'training') {
      trainingAtom.updateThemePhotoSize(themeId, aspectRatio, size);
    } else if (mode === 'cart') {
      cartAtom.updateThemePhotoSize(themeId, aspectRatio, size);
    }
  };

  const updateThemeQuantity = (themeId: string, quantity: number) => {
    if (mode === 'training') {
      trainingAtom.updateThemeQuantity(themeId, quantity);
    } else if (mode === 'cart') {
      cartAtom.updateThemeQuantity(themeId, quantity);
    }
  };
  const updateThemeSelectedModifiers = (
    themeId: string,
    modifiers: Record<string, Record<string, { value: string; color?: string }>>
  ) => {
    if (mode === 'training') {
      trainingAtom.updateThemeSelectedModifiers(themeId, modifiers);
    } else if (mode === 'cart') {
      // Assuming a similar function exists in cartAtom
      cartAtom.updateBulkThemeSelectedModifiers(themeId, modifiers);
    }
  };
  const updateThemePhotoQuantity = (themeId: string, quantity: number) => {
    if (mode === 'training') {
      trainingAtom.updateThemePhotoQuantity(themeId, quantity);
    } else if (mode === 'cart') {
      //TODO: this does not exist on the cartAtom, maybe we should create a photo quantity on the cart
      // cartAtom.updateThemePhotoQuantity(themeId, quantity);
      console.warn('updateThemePhotoQuantity not implemented for cart mode');
    }
  };

  const updateThemeWorkflow = (themeId: string, workflowName: string) => {
    if (mode === 'cart') {
      cartAtom.updateThemeWorkflow(themeId, workflowName);
    }
  };

  const updateThemeGarmentImages = (
    themeId: string,
    garmentImages: { url: string; type: 'top' | 'bottom' | 'one-piece' }[]
  ) => {
    if (mode === 'cart') {
      cartAtom.updateThemeGarmentImages(themeId, garmentImages);
    }
  };

  const bulkAddTheme = (theme: ThemeConfiguration) => {
    if (mode === 'training') {
      // Get current themes from training atom
      const currentThemes = trainingAtom.training.themes || [];
      // Set the new themes array directly
      trainingAtom.setThemes([...currentThemes, theme]);
    } else if (mode === 'cart') {
      cartAtom.addTheme(theme);
    }
  };

  return {
    themes,
    addTheme,
    removeTheme,
    updateThemePhotoSize,
    updateThemeQuantity,
    updateThemeSelectedModifiers,
    updateThemePhotoQuantity,
    updateThemeWorkflow,
    updateThemeGarmentImages,
    bulkAddTheme,
    // Return model-related properties and methods
    model,
    checkModelAvailability,
    handleThemeSelection
  };
}

export default usePhotoPackAction;
